<script setup lang="ts">
import { ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import { cropSignature } from "@/common/utils/image";

import { svgIcon, baseModal } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["save", "refresh", "del"]);

const props = withDefaults(
  defineProps<{
    signature: string | null;
    width: string;
    showActions: boolean;
    showDel: boolean;
  }>(),
  {
    signature: null,
    width: "40rem",
    showActions: false,
    showDel: false,
  }
);

const boxSinature = ref<any>(null);
const canvas = ref<any>(null);
const ctx = ref<any>(null);

const drawing = ref(false);
const showModal = ref(false);

function startDrawing(event: MouseEvent | TouchEvent) {
  drawing.value = true;
  const rect = canvas.value.getBoundingClientRect();

  let x, y;
  if (event instanceof MouseEvent) {
    x = event.clientX - rect.left;
    y = event.clientY - rect.top;
  } else if (event instanceof TouchEvent) {
    const touch = event.touches[0];
    x = touch.clientX - rect.left;
    y = touch.clientY - rect.top;
  }

  ctx.value.beginPath();
  ctx.value.moveTo(x, y);
}

function draw(event: MouseEvent | TouchEvent) {
  if (!drawing.value || !ctx.value) return;

  const rect = canvas.value.getBoundingClientRect();

  let x, y;
  if (event instanceof MouseEvent) {
    x = event.clientX - rect.left;
    y = event.clientY - rect.top;
  } else if (event instanceof TouchEvent) {
    const touch = event.touches[0];
    x = touch.clientX - rect.left;
    y = touch.clientY - rect.top;
  }

  ctx.value.lineTo(x, y);
  ctx.value.stroke();
}

function stopDrawing() {
  drawing.value = false;
}

function clean() {
  if (ctx.value && canvas.value) {
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);
  }
}

function save() {
  if (canvas.value) {
    emit("save", cropSignature(ctx.value, canvas.value));
    showModal.value = false;
    clean();
  }
}

function del() {
  emit("del");
}

async function openSignature() {
  showModal.value = true;
  await nextTick();

  ctx.value = canvas.value.getContext("2d");
  ctx.value.lineWidth = 2;
  ctx.value.lineCap = "round";
  ctx.value.strokeStyle = "black";
}

function closeSignature() {
  showModal.value = false;
  clean();
}
</script>

<template>
  <section class="base-signature">
    <template v-if="props.signature">
      <div class="signature">
        <img :src="props.signature" />
      </div>
    </template>

    <template v-else>
      <div class="empty-signature">
        <div class="signature" @click="openSignature()">
          <img :src="require('@/assets/images/signature.png')" />
        </div>
      </div>
    </template>

    <template v-if="props.showActions">
      <div class="actions">
        <template v-if="props.signature">
          <button class="btn btn-default btn-sm" @click="openSignature()">
            <svg-icon icon="refresh" size="1.5rem" />
            {{ t("common.labels.refresh") }}
          </button>
        </template>

        <template v-if="props.showDel">
          <button class="btn btn-danger btn-sm" @click="del()">
            <svg-icon icon="trash" size="1.5rem" />
            {{ t("common.labels.delete") }}
          </button>
        </template>
      </div>
    </template>

    <base-modal
      :show="showModal"
      :title="t('common.labels.signature').toUpperCase()"
      class="modal-signature"
    >
      <template #content>
        <div ref="boxSinature" class="box-signature">
          <template v-if="boxSinature">
            <canvas
              ref="canvas"
              @mousedown="startDrawing"
              @mouseup="stopDrawing"
              @mousemove="draw"
              @mouseleave="stopDrawing"
              @touchstart.prevent="startDrawing"
              @touchend="stopDrawing"
              @touchmove.prevent="draw"
              :width="boxSinature.offsetWidth"
              height="200"
              style="border: 1px solid #000000"
            />
          </template>
        </div>
      </template>

      <template #actions>
        <button class="btn btn-primary btn-md" @click="save()">
          {{ t("common.labels.save") }}
        </button>
        <button class="btn btn-defatul btn-md" @click="clean()">
          {{ t("common.labels.clean") }}
        </button>
        <button class="btn btn-default btn-md" @click="closeSignature()">
          {{ t("common.labels.close") }}
        </button>
      </template>
    </base-modal>
  </section>
</template>

<style lang="scss" scoped>
.base-signature {
  display: flex;
  flex-direction: column;

  .signature {
    width: calc(100% - (1rem * 2));
    max-width: v-bind("width");
    background: var(--white-off);
    padding: 1rem;
    border-radius: 0.5rem;

    img {
      width: 100%;
    }
  }

  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    margin-block: 1.5rem;
  }

  .empty-signature {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    .signature {
      cursor: pointer;
      display: flex;
      height: 10rem;
      align-items: center;
      justify-content: center;
      background: var(--white-off);
      border-collapse: separate;
      border-spacing: 0.75rem;
      border: 2px dotted var(--border);
      border-radius: 0.5rem;

      img {
        width: 80%;
      }
    }
  }

  canvas {
    cursor: crosshair;
  }
}
</style>
